import { useState } from 'react';
import { Link } from 'react-router';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useAuth0 } from '@auth0/auth0-react';
import { useDashboardApi } from '../Api';
import DashboardLayout from '../components/DashboardLayout';
import SectionHeader from '../components/partials/SectionHeader';

interface InsuranceFormData {
  isDagWealthCustomer: boolean;
  advisorId: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  email: string;
  phone: string;
  country: string;
  state: string;
  insuranceType: string;
  coverageAmount: string;
  notes: string;
}

type FormErrors = Partial<Record<keyof InsuranceFormData, string>>;
type TouchedFields = Partial<Record<keyof InsuranceFormData, boolean>>;

const EMPTY_FORM: InsuranceFormData = {
  isDagWealthCustomer: false,
  advisorId: '',
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  gender: '',
  email: '',
  phone: '',
  country: 'United States',
  state: '',
  insuranceType: '',
  coverageAmount: '',
  notes: '',
};

interface AdvisorInfo {
  salesforceUserId: string;
  name: string;
  email: string;
}

const INSURANCE_TYPES = ['Life', 'Health', 'Auto', 'Home', 'Other'];

function validate(data: InsuranceFormData): FormErrors {
  const errors: FormErrors = {};

  if (!data.firstName.trim()) errors.firstName = 'First name is required.';
  if (!data.lastName.trim()) errors.lastName = 'Last name is required.';

  if (!data.dateOfBirth) {
    errors.dateOfBirth = 'Date of birth is required.';
  } else if (new Date(data.dateOfBirth) >= new Date()) {
    errors.dateOfBirth = 'Date of birth must be in the past.';
  }

  if (!data.phone.trim()) errors.phone = 'Phone number is required.';
  if (!data.state.trim()) errors.state = 'State / Province is required.';
  if (!data.insuranceType) errors.insuranceType = 'Select a type of insurance.';

  return errors;
}

const ALL_TOUCHED: TouchedFields = Object.fromEntries(
  Object.keys(EMPTY_FORM).map((k) => [k, true])
) as TouchedFields;

const BACK_LINK_CLASS =
  'mb-6 inline-flex items-center gap-1.5 text-[12px] font-medium uppercase tracking-[0.12em] text-[var(--dash-text-muted)] transition-colors hover:text-[var(--dash-text)]';

export default function InsurancePage() {
  const { user } = useAuth0();
  const dashboardApi = useDashboardApi();
  const [form, setForm] = useState<InsuranceFormData>({
    ...EMPTY_FORM,
    email: user?.email ?? '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<TouchedFields>({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [advisor, setAdvisor] = useState<AdvisorInfo | null>(null);
  const [advisorFetched, setAdvisorFetched] = useState(false);
  const [advisorLoading, setAdvisorLoading] = useState(false);

  function set<K extends keyof InsuranceFormData>(
    key: K,
    value: InsuranceFormData[K]
  ) {
    const next = { ...form, [key]: value };
    setForm(next);
    if (touched[key]) {
      setErrors(validate(next));
    }
  }

  function handleBlur<K extends keyof InsuranceFormData>(key: K) {
    setTouched((prev) => ({ ...prev, [key]: true }));
    setErrors(validate(form));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(ALL_TOUCHED);
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const { email: _email, ...submitPayload } = form;
      await dashboardApi.post('/dashboard/api/insurance/intake', submitPayload);
      setSubmitted(true);
    } catch {
      setSubmitError(
        'Something went wrong submitting your request. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  function fieldClass(base: string, key: keyof InsuranceFormData) {
    return touched[key] && errors[key] ? `${base} ${base}--error` : base;
  }

  if (submitted) {
    return (
      <DashboardLayout>
        <div className="space-y-9 px-10 py-8">
          <section>
            <Link to="/" className={BACK_LINK_CLASS}>
              <ArrowLeftIcon className="h-3.5 w-3.5" />
              Back to Dashboard
            </Link>
            <SectionHeader>Insurance</SectionHeader>
            <div className="dash-card rounded-[10px] px-8 py-10 text-center">
              <p className="font-serif text-[22px] font-medium leading-snug">
                Thank you for your request
              </p>
              <p className="dash-row__meta mt-3 text-[14px]">
                A specialist will be in touch shortly to discuss your coverage
                options.
              </p>
              <button
                className="dash-form-btn mt-8"
                onClick={() => {
                  setForm({ ...EMPTY_FORM, email: user?.email ?? '' });
                  setErrors({});
                  setTouched({});
                  setSubmitted(false);
                }}
              >
                Submit another request
              </button>
            </div>
          </section>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-9 px-10 py-8">
        <section>
          <Link to="/" className={BACK_LINK_CLASS}>
            <ArrowLeftIcon className="h-3.5 w-3.5" />
            Back to Dashboard
          </Link>
          <SectionHeader>Insurance Quote Request</SectionHeader>
          <div className="dash-card rounded-[10px] px-8 py-8">
            <form onSubmit={handleSubmit} className="space-y-8" noValidate>
              {/* DAG Wealth membership */}
              <fieldset className="dash-form-fieldset">
                <legend className="dash-form-legend">
                  DAG Wealth membership
                </legend>
                <div className="mt-3 flex items-center gap-6">
                  <label className="dash-form-radio-label">
                    <input
                      type="radio"
                      name="isDagWealthCustomer"
                      className="dash-form-radio"
                      checked={!form.isDagWealthCustomer}
                      onChange={() => {
                        setForm((prev) => ({
                          ...prev,
                          isDagWealthCustomer: false,
                          advisorId: '',
                        }));
                      }}
                    />
                    <span>Not a DAG Wealth customer</span>
                  </label>
                  <label className="dash-form-radio-label">
                    <input
                      type="radio"
                      name="isDagWealthCustomer"
                      className="dash-form-radio"
                      checked={form.isDagWealthCustomer}
                      onChange={async () => {
                        setForm((prev) => ({
                          ...prev,
                          isDagWealthCustomer: true,
                        }));
                        if (advisorFetched) return;
                        setAdvisorLoading(true);
                        try {
                          const res = await dashboardApi.get<{
                            advisorAssigned: boolean;
                            advisor: AdvisorInfo | null;
                          }>('/dashboard/api/insurance/advisor');
                          if (res.data.advisorAssigned && res.data.advisor) {
                            setAdvisor(res.data.advisor);
                            setForm((prev) => ({
                              ...prev,
                              advisorId: res.data.advisor!.salesforceUserId,
                            }));
                          }
                        } catch {
                          // Non-fatal — advisor field stays empty
                        } finally {
                          setAdvisorFetched(true);
                          setAdvisorLoading(false);
                        }
                      }}
                    />
                    <span>Yes, I'm a DAG Wealth customer</span>
                  </label>
                </div>

                {form.isDagWealthCustomer && (
                  <div className="dash-form-field mt-5">
                    <p className="dash-form-label">Your advisor</p>
                    <p
                      className={`text-[14px] ${advisorLoading || !advisor ? 'text-[var(--dash-text-faint)]' : 'text-[var(--dash-text)]'}`}
                    >
                      {advisorLoading
                        ? 'Looking up advisor…'
                        : advisor
                          ? advisor.name
                          : 'No advisor on file'}
                    </p>
                  </div>
                )}
              </fieldset>

              {/* Personal information */}
              <fieldset className="dash-form-fieldset">
                <legend className="dash-form-legend">
                  Personal information
                </legend>
                <div className="mt-3 grid grid-cols-2 gap-5">
                  <div className="dash-form-field">
                    <label className="dash-form-label" htmlFor="firstName">
                      First name <span className="dash-form-required">*</span>
                    </label>
                    <input
                      id="firstName"
                      type="text"
                      className={fieldClass('dash-form-input', 'firstName')}
                      value={form.firstName}
                      onChange={(e) => set('firstName', e.target.value)}
                      onBlur={() => handleBlur('firstName')}
                    />
                    {touched.firstName && errors.firstName && (
                      <p className="dash-form-error">{errors.firstName}</p>
                    )}
                  </div>
                  <div className="dash-form-field">
                    <label className="dash-form-label" htmlFor="lastName">
                      Last name <span className="dash-form-required">*</span>
                    </label>
                    <input
                      id="lastName"
                      type="text"
                      className={fieldClass('dash-form-input', 'lastName')}
                      value={form.lastName}
                      onChange={(e) => set('lastName', e.target.value)}
                      onBlur={() => handleBlur('lastName')}
                    />
                    {touched.lastName && errors.lastName && (
                      <p className="dash-form-error">{errors.lastName}</p>
                    )}
                  </div>
                  <div className="dash-form-field">
                    <label className="dash-form-label" htmlFor="dateOfBirth">
                      Date of birth{' '}
                      <span className="dash-form-required">*</span>
                    </label>
                    <input
                      id="dateOfBirth"
                      type="date"
                      className={fieldClass('dash-form-input', 'dateOfBirth')}
                      value={form.dateOfBirth}
                      onChange={(e) => set('dateOfBirth', e.target.value)}
                      onBlur={() => handleBlur('dateOfBirth')}
                    />
                    {touched.dateOfBirth && errors.dateOfBirth && (
                      <p className="dash-form-error">{errors.dateOfBirth}</p>
                    )}
                  </div>
                  <div className="dash-form-field">
                    <label className="dash-form-label" htmlFor="gender">
                      Gender
                    </label>
                    <select
                      id="gender"
                      className="dash-form-select"
                      value={form.gender}
                      onChange={(e) => set('gender', e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Non-binary">Non-binary</option>
                      <option value="Prefer not to say">
                        Prefer not to say
                      </option>
                    </select>
                  </div>
                  <div className="dash-form-field">
                    <label className="dash-form-label" htmlFor="email">
                      Email
                    </label>
                    <input
                      id="email"
                      type="email"
                      className="dash-form-input"
                      value={form.email}
                      readOnly
                    />
                  </div>
                  <div className="dash-form-field">
                    <label className="dash-form-label" htmlFor="phone">
                      Phone <span className="dash-form-required">*</span>
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      className={fieldClass('dash-form-input', 'phone')}
                      value={form.phone}
                      onChange={(e) => set('phone', e.target.value)}
                      onBlur={() => handleBlur('phone')}
                    />
                    {touched.phone && errors.phone && (
                      <p className="dash-form-error">{errors.phone}</p>
                    )}
                  </div>
                  <div className="dash-form-field">
                    <label className="dash-form-label" htmlFor="country">
                      Country <span className="dash-form-required">*</span>
                    </label>
                    <select
                      id="country"
                      className="dash-form-select"
                      value={form.country}
                      onChange={(e) => set('country', e.target.value)}
                    >
                      <option value="United States">United States</option>
                      <option value="Canada">Canada</option>
                    </select>
                  </div>
                  <div className="dash-form-field">
                    <label className="dash-form-label" htmlFor="state">
                      State / Province{' '}
                      <span className="dash-form-required">*</span>
                    </label>
                    <input
                      id="state"
                      type="text"
                      className={fieldClass('dash-form-input', 'state')}
                      placeholder="e.g. California"
                      value={form.state}
                      onChange={(e) => set('state', e.target.value)}
                      onBlur={() => handleBlur('state')}
                    />
                    {touched.state && errors.state && (
                      <p className="dash-form-error">{errors.state}</p>
                    )}
                  </div>
                </div>
              </fieldset>

              {/* Coverage details */}
              <fieldset className="dash-form-fieldset">
                <legend className="dash-form-legend">Coverage details</legend>
                <div className="mt-3 grid grid-cols-2 gap-5">
                  <div className="dash-form-field">
                    <label className="dash-form-label" htmlFor="insuranceType">
                      Type of insurance{' '}
                      <span className="dash-form-required">*</span>
                    </label>
                    <select
                      id="insuranceType"
                      className={fieldClass(
                        'dash-form-select',
                        'insuranceType'
                      )}
                      value={form.insuranceType}
                      onChange={(e) => set('insuranceType', e.target.value)}
                      onBlur={() => handleBlur('insuranceType')}
                    >
                      <option value="">Select a type</option>
                      {INSURANCE_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                    {touched.insuranceType && errors.insuranceType && (
                      <p className="dash-form-error">{errors.insuranceType}</p>
                    )}
                  </div>
                  <div className="dash-form-field">
                    <label className="dash-form-label" htmlFor="coverageAmount">
                      Coverage amount
                    </label>
                    <input
                      id="coverageAmount"
                      type="text"
                      className="dash-form-input"
                      placeholder="e.g. $500,000"
                      value={form.coverageAmount}
                      onChange={(e) => set('coverageAmount', e.target.value)}
                    />
                  </div>
                </div>
                <div className="dash-form-field mt-5">
                  <label className="dash-form-label" htmlFor="notes">
                    Additional notes
                  </label>
                  <textarea
                    id="notes"
                    className="dash-form-textarea"
                    rows={4}
                    placeholder="Anything else we should know…"
                    value={form.notes}
                    onChange={(e) => set('notes', e.target.value)}
                  />
                </div>
              </fieldset>

              <div>
                <button
                  type="submit"
                  className="dash-form-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting…' : 'Submit request'}
                </button>
                {submitError && (
                  <p className="dash-form-error mt-3">{submitError}</p>
                )}
              </div>
            </form>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}
